import type { FacetCounts, JsonFacetBuckets } from '~/utils/solr/facet'
import { deduplicateFacets, extractLabel, extractName, processFacets } from '#shared/utils/solr-facet'
import { labelToNodeTypes } from '~/utils/solr/search-utils'

const CONTENT_TYPES = Object.keys(CONTENT_TYPE_OPTIONS)

function bucketsToFacetField(data?: JsonFacetBuckets): Array<{ value: string, count: number }> | undefined {
  return data?.buckets.map(bucket => ({ value: bucket.val, count: bucket.count }))
}

/**
 * Transforms search facets into UI filter options.
 * Pre-loaded facets provide the available options list.
 * Search facets provide the actual counts — counts are only shown after a search.
 */
export function useSearchFacetOptions(
  searchFacets: ReturnType<typeof useSearchState>['facets'],
  preloadedFacets?: ComputedRef<FacetCounts | undefined>,
) {
  const hasSearchFacets = computed(() => !!searchFacets?.value)

  const contentTypeOptions = computed(() => {
    const nodeFacets = bucketsToFacetField(searchFacets?.value?.nodeType)
    const rawCounts = new Map<string, number>()

    if (nodeFacets) {
      nodeFacets.forEach((item) => {
        rawCounts.set(item.value, item.count)
      })
    }

    return CONTENT_TYPES.map((type) => {
      const types = labelToNodeTypes[type.toLowerCase()] || []
      const count = types.reduce((sum, t) => sum + (rawCounts.get(t) || 0), 0)
      return {
        value: type.toLowerCase(),
        label: hasSearchFacets.value ? `${type} (${count})` : type,
        count,
        disabled: !hasSearchFacets.value || count === 0,
      }
    }).sort((a, b) => b.count - a.count)
  })

  const legalCategoryOptions = computed(() => {
    const searchAreaFacets = bucketsToFacetField(searchFacets?.value?.legal_areas)
    const preloadedAreaFacets = preloadedFacets?.value?.facet_fields?.legal_areas

    if (searchAreaFacets) {
      return processFacets(searchAreaFacets, (item) => {
        const name = extractLabel(item.value)
        return { value: item.value, label: `${name} (${item.count})` }
      }, { includeZero: true })
    }

    if (preloadedAreaFacets) {
      return preloadedAreaFacets.map(item => ({
        value: item.value,
        label: extractLabel(item.value),
        disabled: true,
      }))
    }

    return []
  })

  const authorOptions = computed(() => {
    const searchAuthorFacets = bucketsToFacetField(searchFacets?.value?.author_list)
    const preloadedAuthorFacets = preloadedFacets?.value?.facet_fields?.author_list

    if (searchAuthorFacets) {
      const deduplicated = deduplicateFacets(searchAuthorFacets)
      return processFacets(deduplicated, (item) => {
        const name = extractName(item.value)
        return { value: item.value, label: `${name} (${item.count})` }
      }, { includeZero: true })
    }

    if (preloadedAuthorFacets) {
      const deduplicated = deduplicateFacets(preloadedAuthorFacets)
      return deduplicated.map(item => ({
        value: item.value,
        label: extractName(item.value),
        disabled: true,
      }))
    }

    return []
  })

  return {
    contentTypeOptions,
    legalCategoryOptions,
    authorOptions,
  }
}
